import { useEffect, useState, useCallback } from 'react';
import { useInput, type Validator } from 'react-admin';
import { Box, Typography, Alert, Grid, Paper } from '@mui/material';
import {
  type FormDefinition,
  type FormField,
  type FieldType,
} from './types';
import { FormFieldEditor } from './FormFieldEditor';
import { FieldTypeChipSelector } from './FieldTypeChipSelector';

interface FormBuilderProps {
  source: string;
  label?: string;
  validate?: Validator | Validator[];
}

export const FormBuilder = ({ source, label, validate }: FormBuilderProps) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useInput({ source, validate });

  const [formDefinition, setFormDefinition] = useState<FormDefinition>({
    fields: [],
  });
  const [parseError, setParseError] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  useEffect(() => {
    if (value) {
      try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        if (parsed && typeof parsed === 'object') {
          const fields = (parsed.fields || []).map((f: FormField) => {
            if (f.type === 'EquipmentLookup' && !f.filter) {
              return { ...f, filter: '{"type":"Other"}' };
            }
            return f;
          });
          setFormDefinition({
            ...(parsed.title && { title: parsed.title }),
            fields,
          });
          setParseError(null);
        } else {
          setFormDefinition({ fields: [] });
          setParseError('Invalid form definition format');
        }
      } catch (err) {
        setFormDefinition({ fields: [] });
        setParseError(
          `Failed to parse JSON: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
      }
    } else {
      setFormDefinition({ fields: [] });
    }
  }, [value]);

  const updateFormDefinition = useCallback(
    (next: FormDefinition) => {
      setFormDefinition(next);
      try {
        onChange(JSON.stringify(next));
      } catch (e) {
        console.error('Failed to stringify form definition:', e);
      }
    },
    [onChange],
  );

  const handleAddField = (type: FieldType) => {
    const newField: FormField = {
      type,
      label: '',
      isRequired: false,
    };
    if (['Dropdown', 'Checkboxes', 'RadioButtons'].includes(type)) {
      newField.options = [];
    }
    if (type === 'EquipmentLookup') {
      newField.filter = '{"type":"Other"}';
    }
    updateFormDefinition({
      ...formDefinition,
      fields: [newField, ...formDefinition.fields],
    });
  };

  const handleUpdateField = (index: number, updatedField: FormField) => {
    const fields = [...formDefinition.fields];
    fields[index] = updatedField;
    updateFormDefinition({ ...formDefinition, fields });
  };

  const handleDeleteField = (index: number) => {
    updateFormDefinition({
      ...formDefinition,
      fields: formDefinition.fields.filter((_, i) => i !== index),
    });
  };

  const handleMoveField = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const fields = [...formDefinition.fields];
    const [moved] = fields.splice(fromIndex, 1);
    fields.splice(toIndex, 0, moved);
    updateFormDefinition({ ...formDefinition, fields });
  };

  const handleDragStart = (index: number) => setDraggedIndex(index);

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => setDragOverIndex(null);

  const handleDragEnd = () => {
    if (
      draggedIndex !== null &&
      dragOverIndex !== null &&
      draggedIndex !== dragOverIndex
    ) {
      handleMoveField(draggedIndex, dragOverIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex !== null && draggedIndex !== index) {
      handleMoveField(draggedIndex, index);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          {label}
        </Typography>
      )}

      {parseError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {parseError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Box>
            {formDefinition.fields.map((field, index) => (
              <Box
                key={index}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(e, index)}
                sx={{
                  opacity: draggedIndex === index ? 0.5 : 1,
                  border: dragOverIndex === index ? '2px dashed' : 'none',
                  borderColor:
                    dragOverIndex === index ? 'primary.main' : 'transparent',
                  borderRadius: 1,
                  p: dragOverIndex === index ? 0.5 : 0,
                  transition: 'all 0.2s',
                }}
              >
                <FormFieldEditor
                  field={field}
                  index={index}
                  onUpdate={(f) => handleUpdateField(index, f)}
                  onDelete={() => handleDeleteField(index)}
                  onMoveUp={
                    index > 0 ? () => handleMoveField(index, index - 1) : undefined
                  }
                  onMoveDown={
                    index < formDefinition.fields.length - 1
                      ? () => handleMoveField(index, index + 1)
                      : undefined
                  }
                  isDragging={draggedIndex === index}
                  dragHandleProps={{
                    draggable: true,
                    onDragStart: (e: React.DragEvent) => {
                      e.stopPropagation();
                      handleDragStart(index);
                    },
                  }}
                />
              </Box>
            ))}
          </Box>

          {formDefinition.fields.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, fontStyle: 'italic' }}
            >
              No fields yet. Choose field types on the right to add them.
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            elevation={1}
            sx={{
              p: 2,
              position: 'sticky',
              top: 16,
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Add field types
            </Typography>
            <FieldTypeChipSelector onFieldTypeClick={handleAddField} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
